import { io } from 'socket.io-client';
import { createAction } from '@reduxjs/toolkit';

// ========== MODULE-LEVEL SHARED STATE ==========
let sharedStore = null;
let storeInitResolve = null;
let storeInitPromise = new Promise((resolve) => { storeInitResolve = resolve; });
let storeInitResolved = false;
const instances = new Set(); // Track all instances for store propagation

// Module-level store attachment
function attachSharedStore(store) {
    if (!store || typeof store.dispatch !== 'function') {
        throw new Error('Valid Redux store required');
    }

    if (sharedStore && sharedStore !== store) {
        console.warn('SocketFactory: Replacing existing shared store reference');
    }

    sharedStore = store;

    if (!storeInitResolved) {
        storeInitResolved = true;
        storeInitResolve();
    }

    // Trigger _maybeResolveInit() on ALL instances
    instances.forEach(instance => {
        instance._maybeResolveInit();
    });

    return true;
}

// Module-level store getter
function getSharedStore() {
    return sharedStore;
}

class SocketFactory {
    constructor() {
        this.socket = null;
        this.eventListeners = new Map(); // eventName -> Map<actionId, actionHandler>
        this.socketHandlers = new Map(); // eventName -> socket handler func reference
        this.actionIdCounter = 0; // For generating unique action IDs
        this.isInitialized = false;

        // Instance-specific initialization promise
        this._instanceInitPromise = new Promise((resolve) => { this._instanceInitResolve = resolve; });
        this._instanceInitResolved = false;
        // Instance-specific initialization promise
        this._instanceInitPromise = new Promise((resolve) => { this._instanceInitResolve = resolve; });
        this._instanceInitResolved = false;
        this._deferredCalls = [];
        this.lastConfig = null; // Store last connection config

        // Register this instance
        instances.add(this);
    }

    // === Initialization glue ===
    _maybeResolveInit() {
        // Instance is initialized when BOTH conditions are met:
        // 1. This instance has a socket
        // 2. The shared store has been attached (module-level)
        if (this.socket && storeInitResolved && !this._instanceInitResolved) {
            this.isInitialized = true;
            this._instanceInitResolved = true;
            this._instanceInitResolve();
            // Flush any deferred API calls
            for (const fn of this._deferredCalls) fn();
            this._deferredCalls = [];
        }
    }

    initializeSocket(socketOptions = {}) {
        try {
            const defaultOptions = {
                url: process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001',
                options: {
                    autoConnect: true,
                    reconnection: true,
                    reconnectionDelay: 1000,
                }
            };

            // Always override transport and timeout settings
            const overrideOptions = {
                transports: ['polling', 'websocket'],
                timeout: 20000,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: Infinity,
                upgrade: true,
            };

            const config = {
                ...defaultOptions,
                ...socketOptions,
                options: {
                    ...defaultOptions.options,
                    ...socketOptions.options,
                    ...overrideOptions,
                }
            };

            // Store config for external consumers (e.g. Yjs Provider)
            this.lastConfig = config;



            const newSocket = io(config.url, config.options);
            // If socket already exists, reattach to new socket instance
            this.reattachSocket(newSocket);

            this._maybeResolveInit();
            return true;
        } catch (error) {
            console.error('Socket initialization failed:', error);
            return false;
        }
    }

    // Reattach all existing event handlers to a new socket instance
    reattachSocket(newSocket) {
        if (!newSocket) {
            throw new Error('New socket instance is required for reattachSocket');
        }

        // Store current socket to disconnect it after reattaching
        const oldSocket = this.socket;

        // Reattach all existing event handlers to the new socket
        this.socketHandlers.forEach((handler, eventName) => {
            // Remove from old socket if it exists
            if (oldSocket) {
                oldSocket.off(eventName, handler);
            }
            // Add to new socket
            newSocket.on(eventName, handler);
        });

        // Update reference to new socket
        this.socket = newSocket;

        // Disconnect the old socket if it exists
        if (oldSocket && oldSocket !== newSocket) {
            oldSocket.disconnect();
        }

        // Update connection handlers
        this.socket.on('connect', () => console.log('Socket reconnected'));
        this.socket.on('disconnect', (reason) => {
            console.log('Socket disconnected:', reason);
        });
        return true;
    }

    // Instance-level store attachment (delegates to module-level)
    attachStore(store) {
        try {
            return attachSharedStore(store);
        } catch (error) {
            console.error('Store attachment failed:', error);
            return false;
        }
    }

    // === Deferred-execution wrapper ===
    _ensureInitialized(fn) {
        if (this.isInitialized) {
            return Promise.resolve(fn());
        } else {
            // Wait for BOTH the shared store AND this instance's socket
            return Promise.all([storeInitPromise, this._instanceInitPromise])
                .then(() => fn());
        }
    }

    // --- All methods below here use _ensureInitialized to guarantee ready state ---

    _generateActionId(actionHandler) {
        if (typeof actionHandler === 'string') return actionHandler;

        // For functions, try to use function name or generate unique ID
        if (typeof actionHandler === 'function') {
            if (actionHandler.name && actionHandler.name !== 'anonymous') {
                return actionHandler.name;
            }
            // For anonymous functions, check if we've seen this exact function before
            const existingId = this._functionToIdMap?.get(actionHandler);
            if (existingId) return existingId;
        }

        this.actionIdCounter++;
        const newId = `action_${this.actionIdCounter}`;

        // Track function reference for consistent IDs
        if (typeof actionHandler === 'function') {
            this._functionToIdMap = this._functionToIdMap || new WeakMap();
            this._functionToIdMap.set(actionHandler, newId);
        }

        return newId;
    }

    _createEventHandler(eventName) {
        return (data) => {
            const actionHandlers = this.eventListeners.get(eventName);
            if (!actionHandlers || actionHandlers.size === 0) return;

            const store = getSharedStore();
            if (!store) {
                console.error('SocketFactory: No store available to dispatch actions');
                return;
            }

            actionHandlers.forEach((handlerInfo, actionId) => {
                let actionHandler;
                try {
                    // Extract the actual handler and registration info
                    if (typeof handlerInfo === 'object' && handlerInfo !== null && handlerInfo.handler !== undefined) {
                        actionHandler = handlerInfo.handler;
                    } else {
                        // Fallback for old format
                        actionHandler = handlerInfo;
                    }

                    // Check for Promise (common mistake)
                    if (actionHandler && typeof actionHandler.then === 'function') {
                        console.error(
                            `SocketFactory: Promise detected as handler for event '${eventName}'. Did you forget to await or handle the promise?`,
                            {
                                eventName,
                                actionId,
                                registeredAt: handlerInfo.registeredAt || 'Unknown location',
                                handlerInfo,
                                timestamp: new Date().toISOString()
                            }
                        );
                        return;
                    }

                    if (typeof actionHandler === 'string') {
                        // String action type
                        const action = {
                            type: actionHandler,
                            payload: { eventName, data }
                        };
                        store.dispatch(action);

                    } else if (typeof actionHandler === 'function' && !actionHandler.typePrefix) {
                        // Execute the function and handle the result
                        const result = actionHandler({ eventName, data });

                        // Check if result is a Promise (async function)
                        if (result && typeof result.then === 'function') {
                            console.warn(
                                `SocketFactory: Async function handler for event '${eventName}' returned a Promise. Consider using await or handling the promise properly.`,
                                {
                                    eventName,
                                    actionId,
                                    registeredAt: handlerInfo.registeredAt || 'Unknown location',
                                    timestamp: new Date().toISOString()
                                }
                            );
                            return;
                        }

                        // If function returns undefined/null, treat as callback
                        if (result === undefined || result === null) {
                            // This was a callback function, no dispatch needed
                            return;
                        }

                        // If returns an object with type, treat as action creator
                        if (typeof result === 'object' && result !== null && typeof result.type === 'string' && result.type.trim() !== '') {
                            store.dispatch(result);
                        } else {
                            // Function returned something but it's not a valid action
                            console.warn(
                                `SocketFactory: Function handler for event '${eventName}' returned invalid action:`,
                                {
                                    result,
                                    eventName,
                                    actionId,
                                    registeredAt: handlerInfo.registeredAt || 'Unknown location',
                                    expectedFormat: 'Object with string type property',
                                    timestamp: new Date().toISOString()
                                }
                            );
                        }

                    } else if (typeof actionHandler === 'function' && !!actionHandler.typePrefix) {
                        // Dispatch thunk
                        store.dispatch(actionHandler())
                    }
                    else if (typeof actionHandler === 'object' && actionHandler !== null) {
                        // Plain action object - validate it has a type
                        if (typeof actionHandler.type === 'string' && actionHandler.type.trim() !== '') {
                            const action = {
                                ...actionHandler,
                                payload: { eventName, data, ...actionHandler.payload }
                            };
                            store.dispatch(action);
                        } else {
                            console.warn(
                                `SocketFactory: Invalid action object for event '${eventName}' - missing or invalid type:`,
                                {
                                    actionHandler,
                                    eventName,
                                    actionId,
                                    registeredAt: handlerInfo.registeredAt || 'Unknown location',
                                    timestamp: new Date().toISOString()
                                }
                            );
                        }
                    } else {
                        // Unsupported handler type
                        console.warn(
                            `SocketFactory: Unsupported handler type for event '${eventName}':`,
                            {
                                handlerType: typeof actionHandler,
                                actionHandler,
                                eventName,
                                actionId,
                                registeredAt: handlerInfo.registeredAt || 'Unknown location',
                                timestamp: new Date().toISOString()
                            }
                        );
                    }
                } catch (error) {
                    console.error(
                        `SocketFactory: Handler failed for event '${eventName}', actionId '${actionId}':`,
                        {
                            error: error.message,
                            eventName,
                            actionId,
                            handlerType: typeof actionHandler,
                            actionHandler,
                            registeredAt: handlerInfo.registeredAt || 'Unknown location',
                            timestamp: new Date().toISOString()
                        }
                    );
                }
            });
        };
    }

    on(eventName, actionNameOrCreator) {
        // Capture stack trace at registration time
        const registrationStack = new Error().stack;
        const callerLine = registrationStack.split('\n')[2]; // Get the caller line

        // Check if user is accidentally passing a Promise at registration
        if (actionNameOrCreator && typeof actionNameOrCreator.then === 'function') {
            console.error(
                `SocketFactory: Promise passed to on() for event '${eventName}'. This is likely a mistake.`,
                {
                    eventName,
                    registeredAt: callerLine,
                    fullStack: registrationStack,
                    timestamp: new Date().toISOString()
                }
            );
            return Promise.reject(new Error(`Promise passed to on() for event '${eventName}' at ${callerLine}`));
        }

        // Always return a promise for async safety
        return this._ensureInitialized(() => {
            // Init event map if missing
            if (!this.eventListeners.has(eventName)) {
                this.eventListeners.set(eventName, new Map());
                const socketHandler = this._createEventHandler(eventName);
                this.socketHandlers.set(eventName, socketHandler);
                this.socket.on(eventName, socketHandler);
            }
            const actionId = this._generateActionId(actionNameOrCreator);
            const actionHandlers = this.eventListeners.get(eventName);
            if (typeof actionNameOrCreator === 'string' && actionHandlers.has(actionId)) {
                console.warn(
                    `SocketFactory: Duplicate action '${actionId}' registered for event '${eventName}'. Previous handler will be overwritten.`,
                    { eventName, actionId, timestamp: new Date().toISOString() }
                );
            }

            // Store the handler with registration info for better debugging
            const handlerInfo = {
                handler: actionNameOrCreator,
                registeredAt: callerLine,
                timestamp: new Date().toISOString()
            };

            actionHandlers.set(actionId, handlerInfo);
            return Object.freeze({
                eventName,
                actionId,
                [Symbol.for('socketFactory')]: this
            });
        });
    }

    // ========== API: emit ==========
    emit(eventName, actionType) {
        const actionCreator = createAction(actionType);

        // Create the main function that returns Redux actions
        const emitFunction = (data) => {
            // Fire-and-forget: ensure socket is ready, then emit asynchronously
            (async () => {
                await Promise.all([storeInitPromise, this._instanceInitPromise]);
                if (this.socket) {
                    this.socket.emit(eventName, data);
                }
            })();

            // Always return a serializable Redux action
            return actionCreator({
                eventName,
                data,
                timestamp: Date.now()
            });
        };

        // CRITICAL: Add ALL the properties Redux Toolkit expects

        // 1. Type property (for Redux DevTools and debugging)
        emitFunction.type = actionType;

        // 2. toString method (for string conversion and debugging)
        emitFunction.toString = () => actionType;

        // 3. Match function (used by listener middleware internally)
        emitFunction.match = (action) => {
            return action && action.type === actionType;
        };

        // 4. Optional: Predicate property (some versions expect this)
        Object.defineProperty(emitFunction, 'predicate', {
            value: (action) => action && action.type === actionType,
            writable: false,
            enumerable: false,
            configurable: false
        });

        // 5. Optional: _getType method (internal RTK method)
        emitFunction._getType = () => actionType;

        return emitFunction;
    }

    // ========== API: off ==========
    off(listener) {
        return this._ensureInitialized(() => {
            if (!listener || !listener.eventName || !listener.actionId) {
                console.warn('Invalid listener object passed to off()');
                return this;
            }
            const actionHandlers = this.eventListeners.get(listener.eventName);
            if (!actionHandlers) return this;
            actionHandlers.delete(listener.actionId);
            if (actionHandlers.size === 0) {
                const socketHandler = this.socketHandlers.get(listener.eventName);
                if (socketHandler) {
                    this.socket.off(listener.eventName, socketHandler);
                    this.socketHandlers.delete(listener.eventName);
                }
                this.eventListeners.delete(listener.eventName);
            }
            return this;
        });
    }

    offAll(eventName) {
        return this._ensureInitialized(() => {
            if (this.eventListeners.has(eventName)) {
                const socketHandler = this.socketHandlers.get(eventName);
                if (socketHandler) {
                    this.socket.off(eventName, socketHandler);
                    this.socketHandlers.delete(eventName);
                }
                this.eventListeners.delete(eventName);
            }
            return this;
        });
    }

    cleanup() {
            if (this.socket) {
                this.socketHandlers.forEach((socketHandler, eventName) => {
                    this.socket.off(eventName, socketHandler);
                });
            }
            this._deferredCalls = []
    }

    disconnect() {
        return this._ensureInitialized(() => {
            this.socket.cleanup();
            this.socket.disconnect();
            this.socket = null;
            this.isInitialized = false;
            this.actionIdCounter = 0;

            // Reset instance-specific promise for future re-init
            this._instanceInitPromise = new Promise((resolve) => { this._instanceInitResolve = resolve; });
            this._instanceInitResolved = false;

            return this;
        });
    }

    getEventListeners() {
        const result = {};
        this.eventListeners.forEach((actionHandlers, eventName) => {
            result[eventName] = {};
            actionHandlers.forEach((handlerInfo, actionId) => {
                const handler = handlerInfo.handler || handlerInfo;
                result[eventName][actionId] = {
                    type: typeof handler === 'string' ? handler : handler.name || 'anonymous',
                    registeredAt: handlerInfo.registeredAt || 'Unknown location'
                };
            });
        });
        return result;
    }

    // Status getters
    get initialized() { return this.isInitialized; }
    get connected() { return this.isInitialized && this.socket && this.socket.connected; }

    // --- Promise for external consumers wanting to await readiness ---
    get ready() { return Promise.all([storeInitPromise, this._instanceInitPromise]); }

    // Helper to get configuration for parallel connections
    getLastConfig() {
        return this.lastConfig;
    }
}

// Singleton instance (for backward compatibility)
const socketFactory = new SocketFactory();

// Export both the singleton and the class for multi-instance support
export default socketFactory;
export { SocketFactory, attachSharedStore, getSharedStore };
export const mapSocketFactory = new SocketFactory();
export const meetingSocketFactory = new SocketFactory();
